import { getStateTaxRate } from '../data/states'

type PayFrequency = 'weekly' | 'bi-weekly' | 'semi-monthly' | 'monthly' | 'annually'
type FilingStatus = 'single' | 'married' | 'head-of-household'

// Simplified international tax rates (approximate)
const COUNTRY_TAX_RATES: Record<string, { incomeTax: number, socialTax: number, healthTax: number }> = {
  US: { incomeTax: 0, socialTax: 0.062, healthTax: 0.0145 }, // Calculated progressively
  CA: { incomeTax: 0.15, socialTax: 0.0595, healthTax: 0.0162 },
  GB: { incomeTax: 0.20, socialTax: 0.12, healthTax: 0.0138 },
  AU: { incomeTax: 0.19, socialTax: 0.105, healthTax: 0.02 },
  DE: { incomeTax: 0.25, socialTax: 0.093, healthTax: 0.073 },
  FR: { incomeTax: 0.20, socialTax: 0.11, healthTax: 0.078 },
  ES: { incomeTax: 0.19, socialTax: 0.047, healthTax: 0.041 },
  IT: { incomeTax: 0.23, socialTax: 0.0933, healthTax: 0.0 },
  NL: { incomeTax: 0.37, socialTax: 0.275, healthTax: 0.0 },
  BE: { incomeTax: 0.25, socialTax: 0.131, healthTax: 0.0 },
  SE: { incomeTax: 0.32, socialTax: 0.07, healthTax: 0.0 },
  NO: { incomeTax: 0.22, socialTax: 0.082, healthTax: 0.0 },
  DK: { incomeTax: 0.37, socialTax: 0.08, healthTax: 0.0 },
  CH: { incomeTax: 0.15, socialTax: 0.0595, healthTax: 0.0658 },
  IE: { incomeTax: 0.20, socialTax: 0.04, healthTax: 0.0 },
  NZ: { incomeTax: 0.175, socialTax: 0.0, healthTax: 0.0153 },
  SG: { incomeTax: 0.115, socialTax: 0.17, healthTax: 0.0 },
  JP: { incomeTax: 0.20, socialTax: 0.091, healthTax: 0.0496 },
  KR: { incomeTax: 0.15, socialTax: 0.045, healthTax: 0.0335 },
  IN: { incomeTax: 0.20, socialTax: 0.012, healthTax: 0.0 },
  BR: { incomeTax: 0.15, socialTax: 0.08, healthTax: 0.0 },
  MX: { incomeTax: 0.20, socialTax: 0.0475, healthTax: 0.0 },
  ZA: { incomeTax: 0.18, socialTax: 0.01, healthTax: 0.0 },
}

// 2024 Federal Tax Brackets for US (simplified)
const US_FEDERAL_TAX_BRACKETS = {
  single: [
    { limit: 11000, rate: 0.10 },
    { limit: 44725, rate: 0.12 },
    { limit: 95375, rate: 0.22 },
    { limit: 182100, rate: 0.24 },
    { limit: 231250, rate: 0.32 },
    { limit: 578125, rate: 0.35 },
    { limit: Infinity, rate: 0.37 }
  ],
  married: [
    { limit: 22000, rate: 0.10 },
    { limit: 89050, rate: 0.12 },
    { limit: 190750, rate: 0.22 },
    { limit: 364200, rate: 0.24 },
    { limit: 462500, rate: 0.32 },
    { limit: 693750, rate: 0.35 },
    { limit: Infinity, rate: 0.37 }
  ],
  'head-of-household': [
    { limit: 15700, rate: 0.10 },
    { limit: 59850, rate: 0.12 },
    { limit: 95350, rate: 0.22 },
    { limit: 182100, rate: 0.24 },
    { limit: 231250, rate: 0.32 },
    { limit: 578100, rate: 0.35 },
    { limit: Infinity, rate: 0.37 }
  ]
}

const SOCIAL_SECURITY_WAGE_BASE = 168600 // 2024 US limit

// Convert pay to annual amount
const toAnnual = (amount: number, frequency: PayFrequency): number => {
  const multipliers = {
    weekly: 52,
    'bi-weekly': 26,
    'semi-monthly': 24,
    monthly: 12,
    annually: 1
  }
  return amount * multipliers[frequency]
}

// Convert annual amount back to pay period
const fromAnnual = (amount: number, frequency: PayFrequency): number => {
  const divisors = {
    weekly: 52,
    'bi-weekly': 26,
    'semi-monthly': 24,
    monthly: 12,
    annually: 1
  }
  return amount / divisors[frequency]
}

// Calculate US federal tax using progressive brackets
const calculateUSFederalTax = (annualIncome: number, filingStatus: FilingStatus): number => {
  const brackets = US_FEDERAL_TAX_BRACKETS[filingStatus]
  let tax = 0
  let previousLimit = 0

  for (const bracket of brackets) {
    const taxableInThisBracket = Math.min(annualIncome, bracket.limit) - previousLimit
    if (taxableInThisBracket <= 0) break
    
    tax += taxableInThisBracket * bracket.rate
    previousLimit = bracket.limit
    
    if (annualIncome <= bracket.limit) break
  }

  return tax
}

export interface PaycheckResults {
  grossPay: number
  incomeTax: number
  regionalTax: number
  socialTax: number
  healthTax: number
  totalDeductions: number
  netPay: number
}

export const calculateInternationalPaycheck = (
  grossPay: number,
  payFrequency: PayFrequency,
  filingStatus: FilingStatus,
  countryCode: string,
  stateCode?: string
): PaycheckResults => {
  const taxRates = COUNTRY_TAX_RATES[countryCode] || COUNTRY_TAX_RATES['US']
  
  let incomeTax = 0
  let regionalTax = 0
  let socialTax = 0
  let healthTax = 0

  if (countryCode === 'US') {
    // US-specific calculation with progressive brackets
    const annualGross = toAnnual(grossPay, payFrequency)
    const annualFederalTax = calculateUSFederalTax(annualGross, filingStatus)
    incomeTax = fromAnnual(annualFederalTax, payFrequency)
    
    // State tax
    if (stateCode) {
      const stateTaxRate = getStateTaxRate(stateCode)
      regionalTax = grossPay * stateTaxRate
    }
    
    // FICA taxes
    const periodLimit = fromAnnual(SOCIAL_SECURITY_WAGE_BASE, payFrequency)
    socialTax = Math.min(grossPay, periodLimit) * taxRates.socialTax
    healthTax = grossPay * taxRates.healthTax
  } else {
    // Simplified calculation for other countries
    incomeTax = grossPay * taxRates.incomeTax
    regionalTax = 0 // Most countries have national tax only
    socialTax = grossPay * taxRates.socialTax
    healthTax = grossPay * taxRates.healthTax
  }
  
  const totalDeductions = incomeTax + regionalTax + socialTax + healthTax
  const netPay = grossPay - totalDeductions
  
  return {
    grossPay,
    incomeTax,
    regionalTax,
    socialTax,
    healthTax,
    totalDeductions,
    netPay: Math.max(0, netPay)
  }
}
